import { useEffect, useRef } from 'react';
import { DealStatusConfig, DealStatus, DEAL, MANDATE } from '../store/investor/reducer';
import { StatusDropdownOption } from '../common/_custom/StatusDropdown/StatusDropdown';
import { COLORS } from './constants/colors';
import { DEAL_VIEW_TYPES } from '../store/investor/constants';

type PollingOptions<T> = {
    fetchData: () => Promise<T>;
    onSuccess: (data: T) => void;
    shouldContinue?: (data: T) => boolean;
    interval?: number;
    onError?: (error: unknown) => void;
};

export function usePolling<T>({ fetchData, onSuccess, shouldContinue = () => true, interval = 5000, onError }: PollingOptions<T>) {
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const stopPolling = () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
    };

    useEffect(() => {
        let cancelled = false;

        const poll = async () => {
            try {
                const data = await fetchData();
                if (!cancelled) {
                    onSuccess(data);
                    if (!shouldContinue(data)) stopPolling();
                }
            } catch (err) {
                console.error('Polling error:', err);
                stopPolling();
                if (!cancelled) onError?.(err);
            }
        };

        poll();
        intervalRef.current = setInterval(poll, interval);

        return () => {
            cancelled = true;
            stopPolling();
        };
    }, [fetchData, onSuccess, shouldContinue, interval, onError]);
}

export const formatFollowUpDateTime = (date: Date): string => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, '0');

    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();

    return `${formattedHours}:${formattedMinutes} ${ampm}, ${day} ${month} ${year} (0 days ago)`;
};

type FallbackDealStatusOption = {
    [key: string]: StatusDropdownOption;
};

/**
 * Common utility function to get status dropdown option
 * Used in ConfigTableModal and DealsRow components
 */
export const getStatusDropdownOption = (
    status: string,
    dealStatusConfig: DealStatusConfig | null | undefined,
    selectedCategory: string,
    source?: string,
    fallbackDealStatusOption?: FallbackDealStatusOption,
): StatusDropdownOption => {
    // Default fallback option
    const defaultFallback: StatusDropdownOption = {
        label: 'Deal Shared',
        value: DealStatus.SHARED,
        backgroundColor: COLORS.PURPLE_TINT_50,
        labelColor: 'var(--primary-text-colour)',
        customCaret: {
            height: '14',
            width: '14',
            colour: 'var(--primary-text-colour)',
        },
    };

    // Check if dealStatusConfig is null or undefined
    if (!dealStatusConfig) {
        return fallbackDealStatusOption?.[DealStatus.SHARED] || defaultFallback;
    }

    const isInvestorDeal = source === 'investor-deal' || selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW;
    let defaultStatus = isInvestorDeal ? DealStatus.SHARED : DealStatus.FIRST_DEAL_SHARED;
    if (status === DealStatus.DRAFT) defaultStatus = DealStatus.DRAFT;

    const options = isInvestorDeal ? dealStatusConfig[DEAL] || [] : dealStatusConfig[MANDATE] || [];
    const matchingOption = options.find((option) => option.value === status);
    const defaultMatchingOption = options.find((option) => option.value === defaultStatus);

    return (
        matchingOption ||
        defaultMatchingOption ||
        fallbackDealStatusOption?.[defaultStatus] ||
        fallbackDealStatusOption?.[DealStatus.SHARED] ||
        defaultFallback
    );
};

/**
 * Derives the single-letter avatar fallback initial from a company/person name.
 * Shared by the mobile Lead, Deal and Mandate cards (previously copy-pasted in each).
 * Returns '?' when the name is empty.
 */
export function getInitials(name?: string | null): string {
    if (!name) return '?';
    return (
        name
            .split(' ')
            .map((w) => w[0])
            .join('')
            .substring(0, 1)
            .toUpperCase() || '?'
    );
}

export const getKeysForVisiblityConfig = (defaultKey: string = '', entity: string, actionType: string, dropdownOption?: string) => {
    // Build base key - if defaultKey is empty, don't include the leading dot
    const baseKey = defaultKey ? `${defaultKey}.${entity}.${actionType}` : `${entity}.${actionType}`;
    // Return null for specificKey when no dropdownOption (simpler than returning baseKey)
    const specificKey = dropdownOption ? `${baseKey}.${dropdownOption}` : null;
    return {
        baseKey,
        specificKey,
    };
};

/**
 * Helper function to prepare file data with optional timestamp
 * Used to avoid duplicate file errors by appending timestamp to filename
 * @param fileData - The file data object containing file information
 * @param appendTimestamp - Whether to append timestamp to the filename
 * @param addTimeStampFn - Function to add timestamp to filename
 * @returns Object with originalFileName, processedFileName, and processedFileData
 */
export const prepareFileDataWithTimestamp = (fileData: any, appendTimestamp: boolean, addTimeStampFn: (fileName: string) => string) => {
    const originalFileName = fileData.file.name;
    const processedFileName = appendTimestamp ? addTimeStampFn(originalFileName) : originalFileName;

    // File object properties (name, size, type) are getters, so we explicitly include them
    // Also preserve custom properties like checkForDuplication
    const processedFileData = appendTimestamp
        ? {
              ...fileData,
              file: {
                  name: processedFileName,
                  size: fileData.file.size,
                  type: fileData.file.type,
                  ...(fileData.file.checkForDuplication && { checkForDuplication: fileData.file.checkForDuplication }),
              },
          }
        : fileData;

    return { originalFileName, processedFileName, processedFileData };
};

/**
 * Cell-level disable precedence for listing tables (Leads / Deals / Mandates):
 * an explicit module-level write grant wins outright (e.g. super admin);
 * otherwise the per-column config flag locks the cell; otherwise the
 * module-level disabled visibility flag applies.
 */
export function fieldCellDisabled(columnDisable: boolean | undefined, visibility: { write?: boolean; disabled?: boolean }): boolean {
    if (visibility.write) return false;
    if (columnDisable) return true;
    if (visibility.disabled) return true;
    return false;
}

/** Athena's `{"error": "<code>"}` body on a pre-SSE non-2xx (e.g. 409 clarification_stale), or
 * undefined for any other body shape (a FastAPI 422 `detail` list, an empty body). */
export const parseErrorCode = (responseText?: string): string | undefined => {
    try {
        const code = JSON.parse(responseText ?? '')?.error;
        return typeof code === 'string' ? code : undefined;
    } catch {
        return undefined;
    }
};
